// @flow
import * as intersection from 'lodash.intersection';
import { option } from 'fp-ts';
import { createSelector } from 'reselect';
import { fromNullable, some, type Option } from 'fp-ts/lib/Option';
import {
  TransformedCartResponse,
  ConfigurableAttributes
} from '../../flowtypes/product';
import { addToCartRequest } from '../../services/magento';
import {
  ProductAdt,
  isGiftcard,
  isConfigurable,
  isVirtual,
  isCard,
  getBaseProductPrice,
  getCatalogPriceRules,
  getProductId, isBundle
} from '../../types/product';
import {
  Attribute,
  validateAttribute,
  getAttributeId,
  type $AttributeEntity,
  validateQuantity,
  ValidationErrors,
  ErrorRequiredEmpty,
  optionHasDependentValues
} from '../../types/attributes';
import { prop, nrError } from '../../helpers/functions';
import { fromEmpty, composeK } from '../../helpers/typeclasses';
import { getMagentoFormKey } from '../../helpers/formKey';
import type {
  PriceList,
  ProductPricing,
  TierPrice
} from '../../flowtypes/product';
import {
  sendAnalyticsForStartProject,
  sendAnalyticsForAddToCart
} from './analytics';
import type { PriceObject } from '../../components/ProductPrice';
import { head } from 'fp-ts/lib/Array';
import { setQuantity } from './quantity';

export type MobileAvailability =
  | 'No App No Mobile'
  | 'Yes App No Mobile'
  | 'Yes Mobile'
  | null;

export type $ProductState = {
  sku: ?string,
  name: ?string,
  cartStatus: ?string,
  enterEditor: boolean,
  description: ?string,
  price: ?string,
  addToCartLabel: string,
  detailsHtml: ?string,
  shippingInfoHtml: ?string,
  productType: string,
  configurableAttributes: ConfigurableAttributes,
  mobileAvailability: MobileAvailability
};

const initialState = {
  sku: '',
  name: '',
  enterEditor: false,
  description: '',
  price: '',
  addToCartLabel: 'Add to Cart',
  detailsHtml: '',
  shippingInfoHtml: '',
  cartStatus: '',
  productType: '',
  configurableAttributes: null,
  isMobileFriendly: null,
  mobileAvailability: null
};

/* Constants */
const UPDATE_CART_ACTION = 'PRODUCT/UPDATE_CART_ACTION';
const SET_ATTRIBUTE = 'PRODUCT/CONFIGURABLE_ATTRIBUTES/SET_ATTRIBUTE';
const SET_ATTRIBUTE_ERROR =
  'PRODUCT/CONFIGURABLE_ATTRIBUTES/SET_ATTRIBUTE_ERROR';

const REFRESH_CART_STATUS = 'REFRESH_CART_STATUS';
export const CART_STATUS_FAILURE = 'CART_FAILURE';
export const CART_STATUS_SUCCESS = 'CART_SUCCESS';
export const CART_STATUS_PROCESSING = 'CART_PROCESSING';

/* Actions */
export const cartStatusUpdate = (cartStatus: ?string) => ({
  type: UPDATE_CART_ACTION,
  payload: {
    cartStatus
  }
});

export const refreshCartStatus = () => ({ type: REFRESH_CART_STATUS })

export const setAttributeValue = (key: string, value: ?string) => ({
  type: SET_ATTRIBUTE,
  key,
  value
});

export const setDefaultAttributeValues = () => (
  dispatch: Function,
  getState: Function
) => {
  const { attributes } = getState();
  const priceList = getPriceListSelector(getState());
  if (priceList) {
    const prices = getLowestProductPricing(priceList);
    if (prices && prices.tierPrices) {
      const pricing = prices.tierPrices
        .sort((a, b) => a.quantity - b.quantity)
        .map((tierPrice: TierPrice) => {
          const quantityOption: QuantityOption = {
            label: `${tierPrice.quantity} ($${tierPrice.price.toFixed(
              2
            )} per card)`,
            value: tierPrice.quantity
          };
          return quantityOption;
        });
      const [firstPrice] = pricing || [undefined];
      if (firstPrice) {
        dispatch(setQuantity(firstPrice));
      }
    }
  }
  if (attributes && attributes.length) {
    attributes.map(x => {
      const [firstOption] = x.options || [undefined];
      if (firstOption) {
        dispatch(setAttributeValue(x.id, firstOption.id));
      }
      return null;
    });
  }
};

export const setAttributeError = (attributeId: string, error: ?string) => ({
  type: SET_ATTRIBUTE_ERROR,
  attributeId,
  error
});

const maybeReloadMessages = () => {
  if (typeof window.reloadData === 'function') {
    window.reloadData();
    window.clearInterval(window.reloadDataInterval);
  } else if (!window.reloadDataInterval) {
    waitForReloadDataMagento();
  }
};

const waitForReloadDataMagento = () => {
  // eslint-ignore-next-line
  window.reloadDataInterval = window.setInterval(maybeReloadMessages, 200);
};

const showMagentoMessages = () => {
  try {
    maybeReloadMessages();
  } catch (e) {
    console.log(e);
  }
};

const dispatchAttributeError = (dispatch, attr, err) => {
  dispatch(setAttributeError(getAttributeId(attr), err));
  return false;
};

const resetAttributeError = (dispatch, attr, isValid) => {
  dispatch(setAttributeError(getAttributeId(attr), null)); // Unset existing errors
  return isValid;
};

const getValueText = attr =>
  option
    .fromNullable(attr.options)
    .map(os => os.filter(o => o.id === attr.value))
    .chain(head)
    .map(o => o.title)
    .getOrElseValue('');

// We need to make sure that options that are not required because of dependent options are made required
// When their depended on option has the correct value
export const validateDependentOptions = (dispatch, attributes) =>
  option
    .fromNullable(attributes)
    .map(attrs =>
      attrs
        // Filter out attributes that do not have a dependent option
        .filter(attr => attr.depends_on_option && attr.depends_on_option !== '')
        .filter(attr => attr.depends_on_values && attr.depends_on_values !== '')
        // Filter out attributes whose depended on option does not have one of the required `depends_on_value`
        .filter(
          attr =>
            attributes
              // Get the attribute that matches this depended on option
              .filter(_attr => _attr.title === attr.depends_on_option)
              // Check if the attribute has a value that makes enables this attribute
              .filter(_attr =>
                attr.depends_on_values.split(',').includes(getValueText(_attr))
              ).length > 0
        )
    )
    .map(attrs =>
      attrs.length === 0
        ? true
        : attrs
            // Figure out of the required dependent options value a value
            .reduce(
              (isValid, attr) =>
                !attr.value || attr.value === ''
                  ? dispatchAttributeError(
                      dispatch,
                      Attribute(attr),
                      ValidationErrors[ErrorRequiredEmpty]
                    )
                  : resetAttributeError(dispatch, Attribute(attr), isValid),
              true
            )
    )
    .getOrElseValue(true);

export const validateDependentOptionValues = (dispatch, attributes) =>
  option
    .fromNullable(attributes)
    .map(attrs =>
      attrs
        // Filter out attributes that do not have a value that has a dependent option
        .filter(
          attr =>
            attr.options &&
            attr.options.reduce(
              (a, c) => !!(a && optionHasDependentValues(c)),
              true
            )
        )
    )
    .map(attrs =>
      attrs.length === 0
        ? true
        : attrs.reduce((isValid, attr) => {
            // Get the value that is selected
            const v = head(
              attr.options.filter(o => o.value === attr.value)
            ).getOrElseValue(false);
            if (!v) {
              return dispatchAttributeError(
                dispatch,
                Attribute(attr),
                ValidationErrors[ErrorRequiredEmpty]
              );
            }
            // Check if the v.depends_on_option has a value that is in v.depends_on_values
            const doa = head(
              attributes.filter(_attr => _attr.title === v.depends_on_option)
            ).getOrElseValue(false);
            if (!doa) {
              dispatchAttributeError(
                dispatch,
                Attribute(attr),
                ValidationErrors[ErrorRequiredEmpty]
              );
              return dispatchAttributeError(
                dispatch,
                Attribute(doa),
                ValidationErrors[ErrorRequiredEmpty]
              );
            }
            return !v.depends_on_values.split(',').includes(getValueText(doa))
              ? dispatchAttributeError(
                  dispatch,
                  Attribute(attr),
                  ValidationErrors[ErrorRequiredEmpty]
                )
              : resetAttributeError(dispatch, Attribute(attr), isValid);
          }, true)
    )
    .getOrElseValue(true);

// Request add to cart and updated state while processing and upon success/failure
export const addToCart = () => (dispatch: Function, getState: Function) => {
  dispatch(cartStatusUpdate(CART_STATUS_PROCESSING));

  const state = getState();
  const product = ProductAdt(state.rawProduct);

  const { attributes } = state;
  const isValidConfiguration: boolean =
    validateQuantity(state.product.priceList, state.quantity.value)(dispatch) &&
    attributes.map(Attribute).reduce(
      (isValid, attr: $AttributeEntity) =>
        option
          .fromNullable(validateAttribute(attr)) // get validation result on attribute
          .map(err => dispatchAttributeError(dispatch, attr, err)) // dispatch error
          .getOrElse(() => resetAttributeError(dispatch, attr, isValid)),
      true
    ) &&
    validateDependentOptions(dispatch, attributes) &&
    validateDependentOptionValues(dispatch, attributes);

  if (!isValidConfiguration) {
    dispatch(cartStatusUpdate(null));
    return;
  }

  getMagentoFormKey().then(formKey => {
    addToCartRequest(formKey)(product)(state)
      .then((cartResp: TransformedCartResponse) => {
        showMagentoMessages();
        if (cartResp.error !== '') {
          dispatch(cartStatusUpdate(CART_STATUS_FAILURE));
          return;
        }

        if (cartResp.redirectTo !== '') {
          dispatch(cartStatusUpdate(CART_STATUS_SUCCESS));
          if (cartResp.redirectTo !== '/checkout/cart') {
            dispatch(sendAnalyticsForStartProject());
          }
          window.location = cartResp.redirectTo;
          return;
        }

        dispatch(sendAnalyticsForAddToCart());
        dispatch(cartStatusUpdate(CART_STATUS_SUCCESS));
        setTimeout(() => {
          dispatch(cartStatusUpdate(null));
        }, 3000);
      })
      .catch(e => {
        nrError(e);
        console.log('error', e);
        showMagentoMessages();
        dispatch(cartStatusUpdate(CART_STATUS_FAILURE));
      });
  });
};

/* Reducer */
export default function reducer(
  state: $ProductState = initialState,
  action: Object
) {
  switch (action.type) {
    case UPDATE_CART_ACTION:
      return {
        ...state,
        cartStatus: action.payload.cartStatus
      };
    default: {
      return state;
    }
  }
}

/* Selectors */
export const productSelector = createSelector(
  state => state.rawProduct,
  rawProduct => ProductAdt(rawProduct)
);

export const MOBILE_DISPLAY_UPLOAD_PHOTOS_CTA = 1;
export const MOBILE_DISPLAY_NEW_APP_DOWNLOAD_PROMPT_CTA = 2;
export const MOBILE_DISPLAY_SHOW_ADD_BUTTON = 3;
export const MOBILE_DISPLAY_LEGACY_CTA = 4;

const NO_APP_NO_MOBILE = 'No App No Mobile';
const YES_APP_NO_MOBILE = 'Yes App No Mobile';
const YES_APP = 'Yes App';

export const mobileAvailabilitySelector = createSelector(
  state => state.product.mobileAvailability,
  state => state.product.isMobileFriendly,
  (mobileAvailability: MobileAvailability, isMobileFriendly) => {
    switch (true) {
      case mobileAvailability === YES_APP_NO_MOBILE:
        return MOBILE_DISPLAY_NEW_APP_DOWNLOAD_PROMPT_CTA;
      case mobileAvailability === NO_APP_NO_MOBILE ||
        (!mobileAvailability && !isMobileFriendly):
        return MOBILE_DISPLAY_UPLOAD_PHOTOS_CTA;
      case mobileAvailability === YES_APP:
        return isMobileFriendly
          ? MOBILE_DISPLAY_SHOW_ADD_BUTTON
          : MOBILE_DISPLAY_NEW_APP_DOWNLOAD_PROMPT_CTA;
      case isMobileFriendly:
        return MOBILE_DISPLAY_SHOW_ADD_BUTTON;
      case !isMobileFriendly:
        return MOBILE_DISPLAY_UPLOAD_PHOTOS_CTA;
      default:
        return MOBILE_DISPLAY_LEGACY_CTA;
    }
  }
);

const attributesSelector = state => state.attributes;

const getAttribute = (attributeCode: string) => attrs =>
  option
    .fromNullable(attrs)
    .map(_attrs =>
      _attrs.filter(a => a.code === attributeCode || a.title === attributeCode)
    )
    .chain(a => head(a));

const getCardQuantityAttribute = (attrs): Object =>
  getAttribute('card_quantity')(attrs);
const getEnvelopeAttribute = (attrs): Object => getAttribute('envelope')(attrs);
const getEnvelopeAddressingAttribute = (attrs): Object =>
  getAttribute('envelope_addressing')(attrs);
const hasCardQuantityAttribute = (product, attrs): boolean =>
  isConfigurable(product) && getCardQuantityAttribute(attrs).isSome();

const getFinalPrice = composeK(
  fromEmpty,
  prop('amount'),
  prop('finalPrice'),
  prop('prices')
);
const getOldPrice = composeK(
  fromEmpty,
  prop('amount'),
  prop('oldPrice'),
  prop('prices')
);

const getFinalPriceSelector = state =>
  prop('configuration')(state)
    .chain(getFinalPrice)
    .map(parseFloat)
    .getOrElseValue(parseFloat(state.product.price));

const getOldPriceSelector = state =>
  prop('configuration')(state)
    .chain(getOldPrice)
    .map(parseFloat)
    .getOrElseValue(state.product.price);

const getQuantitySelector = state => state.quantity;
const getPriceListSelector = state => state.product.priceList;

export const stockStatusSelector = (state: Object): Option<boolean> =>
  option
    .fromNullable(state.product.inStock)
    .chain((x: boolean) => (x === false ? option.some(x) : option.none));

export const livePreviewAttributesSelector = (state: {
  product: Object
}): Array<string> => state.product.livePreviewAttributes || [];
export const skuSelector = (state: { product: Object }) => state.product.sku;

// holidayNote is either null or a string based on the initial state so this just safely retrieves the note
export const holidayNoteSelector = (state: {
  product: Object
}): Option<string> => prop('holidayNote')(state.product);

const lowestPriceTiers = {};
export const getLowestPriceTier = (productPricing: ProductPricing) => {
  if (
    Object.prototype.hasOwnProperty.call(lowestPriceTiers, productPricing.id)
  ) {
    return lowestPriceTiers[productPricing.id];
  }
  const index = 0;
  lowestPriceTiers[productPricing.id] = productPricing.tierPrices.sort(
    (a: TierPrice, b: TierPrice) => a.price - b.price
  )[index];
  return lowestPriceTiers[productPricing.id];
};

let lowestProductPricing: ProductPricing | boolean = false;
export const getLowestProductPricing = (priceList: PriceList) => {
  if (lowestProductPricing) {
    return lowestProductPricing;
  }
  const _priceList = JSON.parse(JSON.stringify(priceList));
  // This means we are looking at a simple product, so it is already the lowest product pricing
  if (!Array.isArray(_priceList)) {
    lowestProductPricing = _priceList;
    return lowestProductPricing;
  }
  const index = 0;
  lowestProductPricing = _priceList.sort(
    (a: ProductPricing, b: ProductPricing) =>
      getLowestPriceTier(a).price - getLowestPriceTier(b).price,
  )[index];
  return lowestProductPricing;
};

const getCustomOptionPriceAdd = (attrs) => {
  try {
    return attrs
      .map(attr => (
        attr.value === ''
          ? 0
          : option
            .fromNullable(
              attr.options.find(
                _option => attr.value === _option.option_type_id,
              ),
            )
            .map(t => (t.price ? parseFloat(t.price) : 0))
            .getOrElseValue(0)
      ))
      .reduce((prev, curr) => parseFloat(prev) + parseFloat(curr));
  } catch (e) {
    nrError(e);
  }
  return 0;
};

export const getSelectedCustomOptions = (attrs: Array<Object>): Array<Object> =>
  option
    .fromNullable(attrs)
    .map(() =>
      attrs
        .filter(a => !a.code)
        .map(a => a.options.filter(v => a.value === v.id)[0] || {})
    )
    .getOrElseValue([]);

const getAttributePriceAdd = attrs => {
  const regex = new RegExp('^.*\\$([0-9\\.]*).*$');
  try {
    return attrs
      .map(attr =>
        option
          .fromNullable(
            attr.value === ''
              ? null
              : attr.options.find(_option => attr.value === _option.id)
          )
          .map(h => (h.hover_price !== '' ? h.hover_price : h.label))
      )
      .map(hoverPrice => (regex.exec(hoverPrice) || [0, 0])[1])
      .reduce((prev, curr) => parseFloat(prev) + parseFloat(curr));
  } catch (e) {
    nrError(e);
    return 0;
  }
};

const getFirstCapture = a => a[1];
const captureAsFloat = (r, v) =>
  option
    .fromNullable(r.exec(v))
    .map(getFirstCapture)
    .map(parseFloat)
    .getOrElseValue(false);
const captureFromLabelAsFloat = r => o => captureAsFloat(r, o.label);

const getSelectedOption = getAttributeFn => attrs =>
  getAttributeFn(attrs)
    .filter(a => a.value || false)
    .map(a => a.options.filter(o => a.value === o.id))
    .chain(os => head(os));

const getCardQuantityValue = attrs =>
  getSelectedOption(getCardQuantityAttribute)(attrs).map(o =>
    captureFromLabelAsFloat(/^([0-9]*)/)(o)
  );

const getCardQuantityPrice = attrs =>
  getSelectedOption(getCardQuantityAttribute)(attrs).map(o =>
    captureFromLabelAsFloat(/^[0-9]* \(\$([0-9.]*).*\)/)(o)
  );

const getOptionPrice = o => parseFloat(o.price);

const getEnvelopePrice = attrs =>
  getSelectedOption(getEnvelopeAttribute)(attrs).map(
    o => captureAsFloat(/^\$([0-9.]*)/, o.hover_price) || getOptionPrice(o) || 0
  );

const getEnvelopeAddressingPrice = attrs =>
  getSelectedOption(getEnvelopeAddressingAttribute)(attrs).map(o =>
    captureFromLabelAsFloat(/^.*\(\$([0-9.]*)/)(o)
  );

const getFullEnvelopePrice = attrs =>
  getEnvelopeAddressingPrice(attrs).getOrElseValue(0) +
  getEnvelopePrice(attrs).getOrElseValue(0);

const allAttributesSelected = attrs =>
  attrs.reduce((a, c) => !!(a && c.value && c.value !== ''), true);

export const getLegacyCardPricing = (
  product,
  attrs,
  finalPricePer,
  quantity
): PriceObject => ({
  isCard: true,
  isConfigurable: true,
  productPrice: getCardQuantityPrice(attrs).getOrElseValue(
    quantity.value === 1 ? getBaseProductPrice(product) : finalPricePer
  ),
  envelopePrice: getFullEnvelopePrice(attrs),
  quantity: getCardQuantityValue(attrs).getOrElseValue(quantity.value),
  displayFrom:
    quantity.value === 1 &&
    !(getAttributePriceAdd(attrs) + getCustomOptionPriceAdd(attrs) > 0)
});

const getPriceTier = (quantity, priceList: PriceList) =>
  quantity === 1
    ? getLowestPriceTier(getLowestProductPricing(priceList)).price
    : option
        .fromNullable(
          priceList.map((pl: ProductPricing) =>
            head(
              pl.tierPrices
                .filter(t => t.quantity === quantity)
                .map(t => t.price)
            ).getOrElseValue(0)
          )
        )
        .chain(l => Math.min(...l));

export const getAdvancedCardPricing = (
  product,
  attrs,
  quantity,
  priceList
): PriceObject => ({
  isCard: true,
  isConfigurable: false,
  productPrice: getPriceTier(quantity.value, priceList)
    ? getPriceTier(quantity.value, priceList)
    : getBaseProductPrice(product),
  productPriceAdd:
    getAttributePriceAdd(attrs) +
    getCustomOptionPriceAdd(attrs) -
    getFullEnvelopePrice(attrs),
  envelopePrice: getFullEnvelopePrice(attrs),
  quantity: getCardQuantityValue(attrs).getOrElseValue(quantity.value),
  displayFrom: quantity.value === 1
});

export const getOtherCardPricing = (
  product,
  attrs,
  finalPricePer,
  oldPrice,
  quantity,
  priceList
): PriceObject => {
  return {};
};

export const getGiftCardPricing = (product, attrs) => ({
  basePrice: getBaseProductPrice(product),
  productPrice: fromNullable(attrs)
    .map(att => att.filter(a => a.id === 'giftcard_amount')[0])
    .chain(prop('value'))
    .getOrElseValue(getBaseProductPrice(product)),
  quantity: 1
});

export const getOtherConfigurableProductPricing = (
  product,
  attrs,
  finalPricePer,
  oldPrice,
  quantity,
  priceList
) => ({
  isConfigurable: true,
  basePrice: getBaseProductPrice(product),
  productPrice: getBaseProductPrice(product),
  productPriceAdd:
    some(oldPrice - getBaseProductPrice(product))
      .filter(v => v > 0)
      .getOrElseValue(0) + getCustomOptionPriceAdd(attrs),
  quantity: quantity && quantity.value ? quantity.value : 1,
  specialPrice: 0,
  displayFrom:
    !(getAttributePriceAdd(attrs) + getCustomOptionPriceAdd(attrs) > 0) &&
    !allAttributesSelected(attrs)
});

export const getOtherSimpleProductPricing = (
  product,
  attrs,
  finalPricePer,
  oldPrice,
  quantity,
  priceList
) => ({
  isConfigurable: false,
  basePrice: getBaseProductPrice(product),
  productPrice: getBaseProductPrice(product),
  productPriceAdd: getCustomOptionPriceAdd(attrs),
  quantity: quantity.value ? quantity.value : 1,
  isStandalone: !attrs.length > 0 && !priceList,
  displayFrom:
    quantity.value === 1 &&
    !(getAttributePriceAdd(attrs) + getCustomOptionPriceAdd(attrs) > 0) &&
    !allAttributesSelected(attrs)
});

export const getPossibleProductIds = (product, attrs) =>
  option
    .fromNullable(attrs)
    .map(_attrs =>
      _attrs
        // Make sure we have a selected attribute
        .filter(attr => attr.value !== '')
        // Make sure the attribute has options key
        .filter(attr => option.fromNullable(attr.options).getOrElseValue(false))
        // Make sure there are actual options
        .filter(attr => attr.options.length > 0)
        // Make sure the options have products key
        .filter(attr =>
          option.fromNullable(attr.options[0].products).getOrElseValue(false)
        )
        // Return the products key for the option that matches the attribute's value
        .map(attr =>
          head(
            attr.options.filter(o => o.id === attr.value).map(o => o.products)
          ).getOrElseValue([])
        )
    )
    // Make sure we have at least one selected attribute with product ids
    .filter(ps => ps.length > 0)
    // Intersect the attribute product ids
    .map(ps => intersection(...ps))
    .getOrElseValue([getProductId(product)]);

export const priceSelector = createSelector(
  productSelector,
  attributesSelector,
  getFinalPriceSelector,
  getOldPriceSelector,
  getQuantitySelector,
  getPriceListSelector,
  (
    product,
    attrs,
    finalPricePer,
    oldPrice,
    quantity,
    priceList,
  ): PriceObject => {
    const priceObject: PriceObject = {
      basePrice: getBaseProductPrice(product),
      productPrice: 0,
      productPriceAdd: 0,
      quantity: 0,
      specialPrice: 0,
      isCard: false,
      isSpecial: false,
      isStandalone: false,
      priceSelected: false,
      catalogPriceRules: [],
      possibleProductIds: getPossibleProductIds(product, attrs),
    };

    let productPrice;
    if (isCard(product)) {
      if (isConfigurable(product) && hasCardQuantityAttribute(product, attrs)) {
        productPrice = getLegacyCardPricing(
          product,
          attrs,
          finalPricePer,
          quantity,
        );
      } else if (priceList) {
        productPrice = getAdvancedCardPricing(
          product,
          attrs,
          quantity,
          priceList,
        );
      } else {
        productPrice = getOtherConfigurableProductPricing(
          product,
          attrs,
          finalPricePer,
          oldPrice,
          quantity,
          priceList,
        );
      }
    } else if (isGiftcard(product)) {
      productPrice = getGiftCardPricing(product, attrs);
    } else if (isConfigurable(product)) {
      productPrice = getOtherConfigurableProductPricing(
        product,
        attrs,
        finalPricePer,
        oldPrice,
        quantity,
        priceList,
      );
    } else if (isVirtual(product)) {
      productPrice = {
        quantity: 1,
        displayFrom: false,
        basePrice: getBaseProductPrice(product),
        productPrice: getBaseProductPrice(product),
      };
    } else if (isBundle(product)) {
      productPrice = {
        quantity: 1,
        displayFrom: false,
        basePrice: getBaseProductPrice(product),
        productPrice: getBaseProductPrice(product),
      };
    } else {
      productPrice = getOtherSimpleProductPricing(
        product,
        attrs,
        finalPricePer,
        oldPrice,
        quantity,
        priceList,
      );
    }

    return {
      ...priceObject,
      ...productPrice,
      catalogPriceRules: getCatalogPriceRules(product),
    };
  },
);



// WEBPACK FOOTER //
// ./src/store/modules/product.js